import { PROXY } from "../lib/utils";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import React, { useEffect, useRef, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const isAbortLikeError = (error) =>
    error?.name === "AbortError" ||
    error?.message?.includes?.("BodyStreamBuffer was aborted") ||
    error?.message?.includes?.("aborted");

const MarkAsComplete = ({ mutate, courseId, chapterId, isCompleted, completedBy }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { user, isAuth, accessToken } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const completionControllerRef = useRef(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            completionControllerRef.current?.abort();
        };
    }, []);

    const completionPercentageHandler = async () => {
        if (!isAuth) {
            toast.error(t("auth-like"));
            navigate('/login');
            return;
        }

        try {
            setIsLoading(true);
            completionControllerRef.current?.abort();
            const controller = new AbortController();
            completionControllerRef.current = controller;

            const res = await fetch(`${PROXY}/courses/${courseId}/chapter/${chapterId}/completion`, {
                method: "PATCH",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                signal: controller.signal,
            });

            const result = await res.json();

            if (res.ok) {
                mutate();
            } else {
                toast.error(result.message || t("toast.course-enroll-failed"));
            }

        } catch (err) {
            if (isAbortLikeError(err)) {
                if (process.env.NODE_ENV === "development") {
                    console.warn("Mark-as-complete request was aborted.");
                }
                return;
            }

            console.error(err);
            toast.error( t("toast.course-enroll-error") );
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    };

    // const hasMarked = completedBy?.some(marker => marker?._id === user.id);
    const hasMarked = completedBy?.includes(user.id);

    return (
        <>
            {isCompleted && hasMarked ? (
                <p
                    className="flex justify-between items-center gap-4 align-middle select-none font-sans font-bold text-center text-xs lg:text-sm py-2 px-3 rounded-md bg-indigo-500 text-white border border-indigo-200 shadow-s shadow-teal-900/10 opacity-[0.45] focus:shadow-none active:opacity-[0.85] active:shadow-none w-full lg:w-auto pointer-events-none"
                >
                    { t("Mark-as-Complete") } <FaCheckCircle size={15} color="white" />
                </p>
            ) : (
                <button
                    onClick={completionPercentageHandler}
                    disabled={isLoading}
                    className="flex justify-between items-center gap-4 align-middle select-none font-sans font-bold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-xs lg:text-sm py-2 px-3 rounded-md bg-indigo-500 text-white border border-indigo-200 shadow-s shadow-teal-900/10 hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none w-full lg:w-auto"
                    type="button"
                    data-ripple-light="true"
                >
                    {isLoading ? t("loading") : t("Mark-as-Complete")}
                    <FaCheckCircle size={15} color="white" />
                </button>
            )}
        </>
    );
};

export default MarkAsComplete;
